import React from 'react'
import { 
  CheckCircle, Gift, Calendar, AlertCircle, 
  TrendingUp, Clock, ChevronRight 
} from 'lucide-react'

interface PriorityItem {
  id: string
  label: string
  count: number
  urgency: 'high' | 'medium' | 'low'
  icon: React.ReactNode
  onClick: () => void
}

interface PriorityBarProps {
  pendingVerifications: number
  redemptionQueue: number
  availabilityRequests: number
  overdueChores: number
  onNavigate: (section: string) => void
}

export const PriorityBar: React.FC<PriorityBarProps> = ({
  pendingVerifications,
  redemptionQueue,
  availabilityRequests,
  overdueChores,
  onNavigate
}) => {
  const priorities: PriorityItem[] = [
    {
      id: 'verifications',
      label: 'Pending Verifications',
      count: pendingVerifications,
      urgency: pendingVerifications > 5 ? 'high' : pendingVerifications > 0 ? 'medium' : 'low',
      icon: <CheckCircle className="w-4 h-4" />,
      onClick: () => onNavigate('verifications')
    },
    {
      id: 'redemptions',
      label: 'Redemption Queue',
      count: redemptionQueue,
      urgency: redemptionQueue > 3 ? 'high' : redemptionQueue > 0 ? 'medium' : 'low',
      icon: <Gift className="w-4 h-4" />,
      onClick: () => onNavigate('redemption-queue')
    },
    {
      id: 'availability',
      label: 'Availability Requests',
      count: availabilityRequests,
      urgency: availabilityRequests > 2 ? 'high' : availabilityRequests > 0 ? 'medium' : 'low',
      icon: <Calendar className="w-4 h-4" />,
      onClick: () => onNavigate('availability')
    },
    {
      id: 'overdue',
      label: 'Overdue Chores',
      count: overdueChores,
      urgency: overdueChores > 5 ? 'high' : overdueChores > 0 ? 'medium' : 'low',
      icon: <AlertCircle className="w-4 h-4" />,
      onClick: () => onNavigate('assignments')
    }
  ]

  const getUrgencyColor = (urgency: string, type: 'bg' | 'text' | 'border') => {
    const colors = {
      high: {
        bg: 'bg-red-100',
        text: 'text-red-700',
        border: 'border-red-300'
      },
      medium: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        border: 'border-yellow-300'
      },
      low: {
        bg: 'bg-gray-100',
        text: 'text-gray-600',
        border: 'border-gray-300'
      }
    }
    return colors[urgency as keyof typeof colors][type]
  }

  const totalPriorities = priorities.reduce((sum, p) => sum + p.count, 0)

  if (totalPriorities === 0) {
    return (
      <div className="bg-green-50 border-b border-green-200 px-4 py-3">
        <div className="flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
          <span className="text-green-700 font-medium">All caught up! No pending actions required.</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border-b shadow-sm sticky top-0 z-20">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <Clock className="w-5 h-5 text-gray-600 mr-2" />
            <h3 className="font-medium text-gray-900">Priority Actions</h3>
          </div>
          <span className="text-sm text-gray-500">
            {totalPriorities} item{totalPriorities !== 1 ? 's' : ''} need attention
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          {priorities.map(priority => {
            const hasItems = priority.count > 0
            
            return (
              <button
                key={priority.id}
                onClick={priority.onClick}
                disabled={!hasItems}
                className={`
                  relative flex items-center justify-between p-3 rounded-lg border transition-all
                  ${hasItems 
                    ? `${getUrgencyColor(priority.urgency, 'bg')} ${getUrgencyColor(priority.urgency, 'border')} hover:shadow-md cursor-pointer` 
                    : 'bg-gray-50 border-gray-200 cursor-default opacity-50'
                  }
                `}
              >
                <div className="flex items-center">
                  <div className={`
                    p-2 rounded-full mr-3
                    ${hasItems 
                      ? `${getUrgencyColor(priority.urgency, 'bg')} ${getUrgencyColor(priority.urgency, 'text')}`
                      : 'bg-gray-100 text-gray-400'
                    }
                  `}>
                    {priority.icon}
                  </div>
                  <div className="text-left">
                    <p className={`text-sm font-medium ${hasItems ? getUrgencyColor(priority.urgency, 'text') : 'text-gray-500'}`}>
                      {priority.label}
                    </p>
                    <p className={`text-xs ${hasItems ? getUrgencyColor(priority.urgency, 'text') : 'text-gray-400'} opacity-75`}>
                      {priority.count > 0 ? `${priority.count} waiting` : 'None'}
                    </p>
                  </div>
                </div>
                
                {hasItems && (
                  <div className="flex items-center">
                    <span className={`text-xl font-bold ${getUrgencyColor(priority.urgency, 'text')}`}>
                      {priority.count}
                    </span>
                    <ChevronRight className={`w-4 h-4 ml-1 ${getUrgencyColor(priority.urgency, 'text')}`} />
                  </div>
                )}

                {/* Pulse animation for high priority items */}
                {hasItems && priority.urgency === 'high' && (
                  <span className="absolute top-2 right-2">
                    <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Quick Stats */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
          <div className="flex space-x-4">
            <div className="flex items-center text-sm text-gray-600">
              <span className="font-medium mr-1">Today:</span>
              <span className="text-green-600 font-medium">12 completed</span>
              <span className="mx-2">•</span>
              <span className="text-blue-600 font-medium">8 verified</span>
            </div>
          </div>
          <button 
            onClick={() => onNavigate('dashboard')}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            View Dashboard →
          </button>
        </div>
      </div>
    </div>
  )
}